import { Component, effect, EventEmitter, inject, input, OnInit, output, Output } from '@angular/core';
import { ExportTypeEnum } from '@models/common/export-type.enum';
import { Relationship } from "@models/relationships/relationship";
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import {
  CompanyOfficeholdersGridService
} from "@pages/companies/components/company-profile-page/components/company-officeholders/company-officeholders-grid.service";
import {
  IOfficeholdersPatchModel,
  IOfficeholdersViewModel
} from "@pages/companies/models/company-officeholders-state.interface";
import {
  EditIndividualDinModalComponent
} from "@pages/individuals/components/individual-profile-page/components/edit-individual-din-modal/edit-individual-din-modal.component";
import {
  IndividualEntityDividerComponent
} from "@pages/individuals/components/individual-profile-page/components/individual-entity-divider/individual-entity-divider.component";
import { AgOpenActionComponent } from "@shared/components/grid/ag-cells/common/ag-open-action/ag-open-action.component";
import {
  GridClientSideComponent
} from "@shared/components/grid/components/grid-client-side/grid-client-side.component";
import { ToggleButtonComponent } from "@shared/controls/toggle-button/toggle-button.component";
import { doNothing } from "@shared/utils/doNothing";
import { excelReportTitleStyles } from "@shared/utils/excel-title-for-report";
import { ColDef, ExcelStyle, GridApi, IsFullWidthRowParams, } from "ag-grid-community";
import { GridOptions, RowHeightParams } from "ag-grid-enterprise";
import { ToastrService } from 'ngx-toastr';
import { OfficeholderGroupModel } from "../../../../models/officeholder-group-model";
import { OfficeholderProfileComponent } from "../officeholder-profile/officeholder-profile.component";

@Component({
  selector: 'app-company-officeholders',
  standalone: true,
  imports: [
    OfficeholderProfileComponent,
    ToggleButtonComponent,
    NgbDropdown,
    NgbDropdownMenu,
    NgbDropdownToggle,
    NgbDropdownItem,
    GridClientSideComponent,
  ],
  providers: [CompanyOfficeholdersGridService],
  templateUrl: 'company-officeholders.component.html',
  styleUrl: 'company-officeholders.component.scss'
})
export class CompanyOfficeholdersComponent implements OnInit {
  companyOfficeholdersGridService = inject(CompanyOfficeholdersGridService);

  officeholdersViewModel = input.required<IOfficeholdersViewModel>();
  disableControls = input<boolean>(false);

  patchOfficeholderViewModel = output<IOfficeholdersPatchModel>();

  modalService = inject(NgbModal);
  toastr = inject(ToastrService);

  @Output() ceaseRegisteredAgent = new EventEmitter<Relationship[]>();
  @Output() companyChangePartyName = new EventEmitter<string[]>();
  @Output() changeOfficerAddress = new EventEmitter<string[]>();
  @Output() selectOfficeholders = new EventEmitter<Relationship[]>();
  @Output() exportOfficeholders = new EventEmitter<ExportTypeEnum>();

  checkedOfficeholderModels: OfficeholderGroupModel[] = [];

  ngOnInit(): void {
    this.setColDefs();
  }

  setOfficeholderDetailsState(officeholderDetails: OfficeholderGroupModel | null = null): void {
    this.patchOfficeholderViewModel.emit({ ...this.officeholdersViewModel(), officeholderDetails });
  }

  setIsPastOfficeholdersVisible(isPastOfficeholdersVisible: boolean): void {
    this.patchOfficeholderViewModel.emit({ ...this.officeholdersViewModel(), isPastOfficeholdersVisible });
  }

  editOfficer(officer: Relationship): void {
    if (officer.individual == null) {
      this.toastr.error('Individual was not found for this officer.', 'Error');
      return;
    }

    const modalRef = this.modalService.open(EditIndividualDinModalComponent, { size: 'sm' });
    const instance = modalRef.componentInstance as EditIndividualDinModalComponent;

    instance.individualId = officer.individual.individualId;
    instance.din = officer.individual.din;

    modalRef.result
      .then(
        (newDin: string | null) => {
          officer.individual!.din = newDin ?? '';
        },
        doNothing
      );
  }

  // =====  Grid  =====

  readonly gridOptions: GridOptions = {
    isFullWidthRow: (params: IsFullWidthRowParams<unknown>): boolean => typeof params.rowNode.data == 'string',
    getRowHeight: (params: RowHeightParams): number | undefined => typeof params.data == 'string' ? 24 : undefined,
    fullWidthCellRenderer: IndividualEntityDividerComponent,
  };

  colDefs = this.companyOfficeholdersGridService.colDefs;

  onGridReady(gridApi: GridApi): void {
    this.companyOfficeholdersGridService.setGridApi(gridApi);
  }

  onSelectOfficeholderModelRow(models: (OfficeholderGroupModel | string)[]): void {
    this.checkedOfficeholderModels = models.filter((model) => typeof model != 'string') as OfficeholderGroupModel[];
    this.selectOfficeholders.emit(this.checkedOfficeholderModels.flatMap((model) => model.relationships));
  }

  setColDefs(): void {
    const actionColDef: ColDef = {
      field: '',
      width: 50,
      sortable: false,
      cellClass: 'actions-button-cell',
      cellRenderer: AgOpenActionComponent,
      cellRendererParams: {
        open: this.setOfficeholderDetailsState.bind(this),
      },
    };

    this.companyOfficeholdersGridService.setColDefsWithOpenOfficerAction(
      actionColDef,
      this.setOfficeholderDetailsState.bind(this)
    );
  }

  // =====  Export  =====

  readonly ExportTypeEnum = ExportTypeEnum;
  readonly excelStyles: ExcelStyle[] = excelReportTitleStyles;

  onExportOfficeholders(exportType: ExportTypeEnum): void {
    this.exportOfficeholders.emit(exportType);
  }
}
